import useCurrentUser from 'hooks/data/currentUser/useCurrentUser';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useCallback, useMemo, useRef } from 'react';
import { useLocalStorage } from 'usehooks-ts';

const CHAT_SESSION_TIMEOUT_MS = 8 * 60 * 60 * 1000;
const CHAT_SESSION_STORAGE_KEY = 'ai-agent:global-chat-sessions';

type StoredChatSession = {
  sessionId: string;
  lastMessageAt: number;
};

type StoredChatSessions = Record<string, StoredChatSession | undefined>;

const createStoredSession = (now: number): StoredChatSession => ({
  sessionId: crypto.randomUUID(),
  lastMessageAt: now,
});

const isStoredChatSession = (
  session: StoredChatSession | undefined,
): session is StoredChatSession =>
  typeof session?.sessionId === 'string' &&
  typeof session.lastMessageAt === 'number';

const hasSessionTimedOut = (session: StoredChatSession, now: number) =>
  now - session.lastMessageAt >= CHAT_SESSION_TIMEOUT_MS;

export const useChatSessionManager = () => {
  const { profile } = useCurrentUser();
  const { vehicle } = useLayoutContext();
  const currentUserId = profile.data?._id;
  const currentFirmId = vehicle?.lpFirmId;
  const sessionKey =
    currentUserId && currentFirmId
      ? `${currentUserId}:${currentFirmId}`
      : currentUserId;
  const [storedSessions, setStoredSessions] =
    useLocalStorage<StoredChatSessions>(CHAT_SESSION_STORAGE_KEY, {});
  const fallbackSessionRef = useRef<StoredChatSession>(
    createStoredSession(Date.now()),
  );

  const activeSessionId = useMemo(() => {
    if (!sessionKey) return null;

    const storedSession = storedSessions?.[sessionKey];
    if (!isStoredChatSession(storedSession)) return null;
    if (hasSessionTimedOut(storedSession, Date.now())) return null;

    return storedSession.sessionId;
  }, [sessionKey, storedSessions]);

  const getSessionIdForSend = useCallback(() => {
    const now = Date.now();

    if (!sessionKey) {
      const fallbackSession = fallbackSessionRef.current;
      fallbackSessionRef.current = hasSessionTimedOut(fallbackSession, now)
        ? createStoredSession(now)
        : { ...fallbackSession, lastMessageAt: now };

      return fallbackSessionRef.current.sessionId;
    }

    const storedSession = storedSessions?.[sessionKey];
    const shouldStartNewSession =
      !isStoredChatSession(storedSession) ||
      hasSessionTimedOut(storedSession, now);

    const nextSession = shouldStartNewSession
      ? createStoredSession(now)
      : { ...storedSession, lastMessageAt: now };

    setStoredSessions((currentSessions) => ({
      ...(currentSessions ?? {}),
      [sessionKey]: nextSession,
    }));

    return nextSession.sessionId;
  }, [sessionKey, setStoredSessions, storedSessions]);

  return {
    activeSessionId,
    getSessionIdForSend,
  };
};
